import React, { useEffect, useState } from 'react';
import { createPlateEditor, Plate } from 'platejs/react';
import { Controller, useForm } from 'react-hook-form';
import { clsx } from 'clsx';
import { Button } from 'src/components/ui/button';
import { ProductsType } from 'src/constants/products';
import { PATH_DASHBOARD } from 'src/constants/spa-routes';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useCreateConfigurationSettingsMutation,
  useDeployConfigurationSettingsMutation,
} from 'src/services';
import { useAppSelector } from 'src/store';
import { useLoader } from 'src/provider/LoaderProvider';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { setCreationFlow } from 'src/store/slices/creationFlowSlices';
import { useDispatch } from 'react-redux';
import { CustomTemplateSelect } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStep4Page/CustomTemplateSelect/CustomTemplateSelect';
import { PlateEditor } from 'src/components/PlateEditor/PlateEditor';

export const CreateProjectManualStep4 = ({
  settings,
  activeSettings,
  configurationSettingsPrompt,
  customTemplatesData,
}: {
  settings?: any;
  activeSettings?: any;
  configurationSettingsPrompt?: any;
  customTemplatesData?: any;
}) => {
  const { id, step } = useParams();
  const [searchParams] = useSearchParams();
  const searchType = searchParams.get('type');
  const navigate = useNavigate();
  const loader = useLoader();
  const { projectType } = useAppSelector((s) => s.projectsSlices);
  const projectTypeVar = projectType || searchType;
  const [customTemplateOptions, setCustomTemplateOptions] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const dispatch = useDispatch();
  const [initialPlateValue, setInitialPlateValue] = useState('');
  const [
    createConfigurationSettings,
    { data: createConfigurationSettingsData, isLoading: isLoadingCreateConfigurationSettings },
  ] = useCreateConfigurationSettingsMutation() as any;
  const [
    deployConfigurationSettings,
    {
      isLoading: isLoadingDeployConfigurationSettings,
      isSuccess: isSuccessDeployConfigurationSettings,
    },
  ] = useDeployConfigurationSettingsMutation();

  const { control, setValue, handleSubmit } = useForm<any>({
    defaultValues: {
      text: '',
      template_id: '',
    },
    resolver: yupResolver(
      object({
        text: stringScheme({ required: true }),
      }),
    ),
  });

  useEffect(() => {
    if (isLoadingCreateConfigurationSettings || isLoadingDeployConfigurationSettings) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingCreateConfigurationSettings, isLoadingDeployConfigurationSettings]);

  useEffect(() => {
    if (customTemplatesData) {
      const options = customTemplatesData.map((customTemplate) => {
        return {
          id: `${customTemplate.id}`,
          name: customTemplate.name,
          placeholder: customTemplate.prompt_parts[0].placeholder,
          text: customTemplate.prompt_parts[0].template,
        };
      });

      setCustomTemplateOptions(options);

      if (options[0]) {
        onCustomTemplateChange(options[0]);
        setValue('template_id', options[0]?.id);
      }
    }
  }, [customTemplatesData]);

  useEffect(() => {
    if (createConfigurationSettingsData) {
      dispatch(
        setCreationFlow({
          id: id,
          step: step,
          finished: true,
        }),
      );

      deployConfigurationSettings({
        project_id: id,
        settings_id: createConfigurationSettingsData.id,
      });
    }
  }, [createConfigurationSettingsData]);

  useEffect(() => {
    if (isSuccessDeployConfigurationSettings) {
      next();
    }
  }, [isSuccessDeployConfigurationSettings]);

  const next = () => {
    navigate(
      projectTypeVar === ProductsType.customer
        ? `${PATH_DASHBOARD.PROJECTS}/${id}/control-desk?type=${ProductsType.customer}`
        : `${PATH_DASHBOARD.ROOT}`,
      { replace: true },
    );
  };

  const back = () => {
    navigate(
      projectTypeVar === ProductsType.customer
        ? `${PATH_DASHBOARD.PROJECTS}/${id}/new/${+step - 1}?type=${projectTypeVar}`
        : `${PATH_DASHBOARD.PROJECTS}/${id}?type=${projectTypeVar}`,
      { replace: true },
    );
  };

  const submit = ({ text }) => {
    const prompt = {
      template_id: +selectedTemplate.id,
      input_data: {
        [selectedTemplate.placeholder]: text,
      },
    };

    createConfigurationSettings({ id, body: { prompt } });
  };

  const onCustomTemplateChange = (option) => {
    if (option) {
      setInitialPlateValue(option.text);
      setValue('text', option.text);
      setSelectedTemplate(option);
    }
  };

  return (
    <div className={'w-full flex flex-col gap-5 items-center'}>
      <form
        id={'create-project-step4'}
        className={'w-full flex flex-col gap-5'}
        onSubmit={handleSubmit(submit)}
      >
        <CustomTemplateSelect
          control={control}
          label={'Template:'}
          options={customTemplateOptions}
          onChange={onCustomTemplateChange}
        />
      </form>
      <Controller
        control={control}
        name="text"
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <div className={'flex flex-col w-full gap-2.5'}>
            <PlateEditor
              label={'System Prompt:'}
              initialValue={initialPlateValue}
              onChange={onChange}
              error={error}
              containerClassName={'max-h-[470px] overflow-hidden bg-white'}
              className={'h-[calc(100%-50px)]'}
            />
          </div>
        )}
      />
      <Button
        form="create-project-step4"
        type="submit"
        className="min-w-24 w-full flex flex-row h-12"
      >
        Continue
      </Button>
      <div
        onClick={back}
        className={'text-[#3F60B3] text-xs font-medium cursor-pointer hover:underline'}
      >
        ← Back
      </div>
    </div>
  );
};
